import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import type { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { InsightObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import type { InsightObjectViewState, ContainerProps } from '../../../../common/types/index.tsx';
import {
	normalizeObjectAttributeResponse,
	getUpdatedAttributesAfterCreating,
} from '../../../../common/utils/normalize-state/index.tsx';
import { ObjectAttributeService } from '../../../../services/object-attribute/index.tsx';
import { updateObjectState } from '../update-object-state/index.tsx';

type AttributeUpdate = {
	objectTypeAttributeId: string;
	updatedValues: {
		value: string | null;
	}[];
	onLoad?: () => void;
	onError?: (arg1: Error | ValidationError) => void;
	onSuccess?: (updatedAttribute?: InsightObjectAttribute) => void;
};

const updateAttributeImplementation =
	({
		objectTypeAttributeId,
		updatedValues,
		onLoad,
		onError,
		onSuccess,
	}: Omit<AttributeUpdate, 'attributeId'>): Action<InsightObjectViewState, ContainerProps> =>
	async ({ getState, dispatch }, { workspaceId, objectId, createAnalyticsEvent, onUpdate }) => {
		const { objectDetails } = getState();
		const { editableAttributeTypes } = objectDetails ?? {};

		try {
			if (objectDetails && editableAttributeTypes) {
				onLoad?.();

				const { attributes } = objectDetails;

				const fireSuccessAnalytics = () => {
					fireTrackAnalytics(createAnalyticsEvent({}), 'updateInsightObjectAttribute succeeded', {
						attributeType: attributes.find(
							(attribute) => attribute.objectTypeAttributeId === objectTypeAttributeId,
						)?.type,
					});
				};

				// un-setting an attribute's value triggers a delete of that attribute in the backend
				const isDeletingAttribute = updatedValues.every(
					(update) => update.value == null || update.value === '',
				);
				if (isDeletingAttribute) {
					await ObjectAttributeService.deleteAttribute({
						workspaceId,
						objectId,
						objectTypeAttributeId,
					});

					const updatedAttributes = attributes.filter(
						(attribute) => attribute.objectTypeAttributeId !== objectTypeAttributeId,
					);

					dispatch(updateObjectState({ attributes: updatedAttributes }));

					onSuccess?.();
					onUpdate();
					fireSuccessAnalytics();
				} else {
					const body = {
						objectId,
						objectTypeAttributeId,
						objectAttributeValues: updatedValues,
					};

					// if no attributes with a matching objectTypeAttributeId exist we're creating a new attribute
					const isCreatingAttribute = !attributes.some(
						(attribute) => attribute.objectTypeAttributeId === objectTypeAttributeId,
					);

					const attributeValuesResponse = isCreatingAttribute
						? await ObjectAttributeService.createAttribute({
								workspaceId,
								body,
							})
						: await ObjectAttributeService.updateAttribute({
								workspaceId,
								body,
							});

					const updatedAttributes = isCreatingAttribute
						? getUpdatedAttributesAfterCreating(
								editableAttributeTypes,
								attributes,
								attributeValuesResponse,
							)
						: attributes.map((attribute) =>
								attribute.objectTypeAttributeId === objectTypeAttributeId
									? normalizeObjectAttributeResponse(
											attribute,
											attributeValuesResponse.objectAttributeValues,
										)
									: attribute,
							);

					// @ts-expect-error - TS2322 - Type 'readonly [...InsightObjectAttribute[], InsightObjectAttribute] | (InsightObjectAttribute | ObjectAttributeResponse)[]' is not assignable to type '(ObjectAttributeResponse[] & InsightObjectAttribute[]) | undefined'.
					dispatch(updateObjectState({ attributes: updatedAttributes }));
					fireSuccessAnalytics();

					onSuccess?.(
						updatedAttributes.find((a) => a.objectTypeAttributeId === objectTypeAttributeId),
					);
					onUpdate();
				}
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireCmdbErrorAnalytics({
				meta: {
					id: 'updateInsightObjectAttribute',
					packageName: 'jiraServicedeskInsightObjectDetailsStore',
					teamName: 'falcons',
				},
				error,
			});
			if (onError) {
				onError(error);
			}
		}
	};

export const updateAttribute = ({
	objectTypeAttributeId,
	updatedValues,
	onLoad,
	onError,
	onSuccess,
}: AttributeUpdate): Action<InsightObjectViewState, ContainerProps> =>
	updateAttributeImplementation({
		objectTypeAttributeId,
		updatedValues,
		onLoad,
		onError,
		onSuccess,
	});
